import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "./firebase";
import { areaToFirestore } from "../models/area";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "../services/collections";

/**
 * AreaService
 * Centraliza as operações de banco de dados para o módulo de Áreas.
 */
const AreaService = {
    /**
     * Busca todas as áreas de um tenant.
     */
    getAreas: async (idTenant) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.AREAS),
                orderBy("name", "asc")
            );
            const querySnapshot = await getDocs(q);
            const areas = [];
            querySnapshot.forEach((doc) => {
                areas.push({ id: doc.id, ...doc.data() });
            });
            return areas;
        } catch (error) {
            console.error("Erro ao listar áreas:", error);
            throw error;
        }
    },

    /**
     * Salva ou atualiza uma área.
     */
    save: async (formData, idTenant, authUser, existingAreas = []) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const areaData = areaToFirestore(formData, idTenant, authUser);

        try {
            if (formData.id) {
                // UPDATE
                const areaRef = doc(db, "tenants", idTenant, COLLECTIONS.AREAS, formData.id);
                const oldArea = existingAreas.find(a => a.id === formData.id) || {};
                await updateDoc(areaRef, areaData);

                // Auditoria
                const changes = AuditService.calculateDiff(oldArea, areaData);
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.AREAS,
                    action: AUDIT_ACTIONS.UPDATE,
                    entityId: formData.id,
                    entityName: areaData.name,
                    description: `Atualizou a área "${areaData.name}"`,
                    details: changes
                });

                return { id: formData.id, ...areaData };
            } else {
                // CREATE
                const newDocRef = await addDoc(collection(db, "tenants", idTenant, COLLECTIONS.AREAS), areaData);

                // Auditoria
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.AREAS,
                    action: AUDIT_ACTIONS.CREATE,
                    entityId: newDocRef.id,
                    entityName: areaData.name,
                    description: `Criou a área "${areaData.name}"`
                });

                return { id: newDocRef.id, ...areaData };
            }
        } catch (error) {
            console.error("Erro no AreaService.save:", error);
            throw error;
        }
    },

    /**
     * Remove uma área.
     */
    delete: async (areaId, idTenant, authUser, areaName) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            await deleteDoc(doc(db, "tenants", idTenant, COLLECTIONS.AREAS, areaId));

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.AREAS,
                action: AUDIT_ACTIONS.DELETE,
                entityId: areaId,
                entityName: areaName,
                description: `Excluiu a área "${areaName || areaId}"`
            });

            return true;
        } catch (error) {
            console.error("Erro no AreaService.delete:", error);
            throw error;
        }
    }
};

export default AreaService;
